import { CartItem } from "../typings/interfaces/pos/cart-item";
import { ISpecialChargeConfig } from "../typings/interfaces/responses/special-charge-response";

type SpecialChargeItem = Pick<
  CartItem,
  "qty" | "unitPrice" | "alterationItems" | "alterationOnly" | "redoDetails" | "serviceType"
>;

const round2 = (value: number) => Math.round(value * 100) / 100;

const charge = (rule: ISpecialChargeConfig | undefined, price: number, count: number) => {
  if (!rule || !(rule.amount > 0) || count <= 0) return 0;
  return rule.chargeType === "PERCENTAGE" ? (price * rule.amount) / 100 : rule.amount * count;
};

// What one item costs extra on a Special ticket. The item's service rule
// covers the garment: a flat amount per garment or a percentage of its base
// price (not its upcharges or spots). The Alterations rule covers its
// alteration lines the same way. Redo is free; Alteration Only has no
// garment charge.
export const getSpecialCharge = (
  item: SpecialChargeItem,
  rules: ISpecialChargeConfig[] | null | undefined
) => {
  if (!rules?.length || item.redoDetails) return 0;
  const serviceRule = rules.find(
    (rule) => !rule.alterations && rule.categoryId === item.serviceType?.id
  );
  const alterationRule = rules.find((rule) => rule.alterations);
  const alterations = (item.alterationItems ?? []).filter((alt) => (alt?.unitPrice ?? 0) > 0);

  const garment = item.alterationOnly
    ? 0
    : charge(serviceRule, item.unitPrice * item.qty, item.qty);
  const alteration = charge(
    alterationRule,
    alterations.reduce((acc, alt) => acc + (alt.unitPrice ?? 0), 0),
    alterations.length
  );
  return round2(garment + alteration);
};

// An item's Special charge inside a session. Regular sessions pay none.
// Without the rules loaded (e.g. a reprint), the charge saved on the item at
// check-in is used.
export const sessionSpecialCharge = (
  item: SpecialChargeItem & Pick<CartItem, "specialCharge">,
  isSpecial: boolean | undefined,
  rules: ISpecialChargeConfig[] | null | undefined
) => {
  if (!isSpecial) return 0;
  return rules?.length ? getSpecialCharge(item, rules) : item.specialCharge ?? 0;
};
